// UI Home: render day card + mini chart peso + plan tiles (setTile/updateDL)
// + profile badge header. PR-6 (ADR-0005). Estratto a parità funzionale.
//
// Dipendenze ESM:
//   ui-chat.js → getBusy (per disabilitare bottoni 'Riprova' durante busy)
//
// Dipendenze runtime (window.* / global scope chain):
//   window.S, window.currentUser, window.PS
//   openPlan (ui-plans inline, future PR-7) → window.openPlan
//   retryPlan (window.retryPlan inline, esposto in coach/index.html)
//   Chart (Chart.js CDN globale)

import { getBusy } from './ui-chat.js';
import {
  getTodayWorkout,
  countWorkoutSessionsThisWeek,
  computeStretchStreak,
  isStretchDayCompleteOn,
  getStretchDayIdxFor,
  countMealsThisWeek,
  totalMealsInPlan,
  totalShoppingItems,
  countShoppingCompletions,
  isMealPlanEnded
} from './plan-helpers.js';

// ─── HOME DAY CARD v9 ──────────────────────────────────────────────────────
// Welcome card eliminata: greeting+data assorbiti dalla topbar (.hdr-center),
// info peso/target/settimana assorbiti dal chart header (.home-chart-info-inline).
// La function mantiene il nome storico per back-compat con caller esistenti.
export function renderHomeDayCard() {
  const days = ['domenica','lunedì','martedì','mercoledì','giovedì','venerdì','sabato'];
  const months = ['gennaio','febbraio','marzo','aprile','maggio','giugno','luglio','agosto','settembre','ottobre','novembre','dicembre'];
  const now = new Date();
  const dayName = days[now.getDay()];
  const dateLong = `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${now.getDate()} ${months[now.getMonth()]}`;
  const prefix = now.getDay() === 0 ? 'Buona' : 'Buon';
  const nome = window.S.profile?.nome || '';

  // ── Popola topbar .hdr-center (greeting + data) ──
  const hdrCenter = document.getElementById('hdrCenter');
  const hdrDate = document.getElementById('hdrDate');
  const hdrGreet = document.getElementById('hdrGreet');
  if (hdrCenter && hdrDate && hdrGreet) {
    hdrDate.textContent = dateLong;
    if (nome) {
      hdrGreet.innerHTML = `${prefix} ${dayName}, <b>${nome}</b>`;
      hdrCenter.style.display = '';
    } else {
      // Senza profilo (onboarding pending) niente greeting personalizzato
      hdrCenter.style.display = 'none';
    }
  }

  // ── Popola chart header info inline (peso/target/settimana) ──
  // Sostituisce la vecchia .home-day-meta dentro la welcome card.
  const chartInfo = document.getElementById('homeChartInfo');
  if (chartInfo) {
    const parts = [];
    const m = window.S.lastMeasurement;
    let peso = null, deltaHtml = '';
    if (m && m.peso_kg != null) {
      peso = m.peso_kg;
      const sorted = [...window.S.measurements].filter(x => x.peso_kg != null).sort((a,b) => new Date(b.measured_at) - new Date(a.measured_at));
      if (sorted.length >= 2) {
        const diff = +(sorted[0].peso_kg - sorted[1].peso_kg).toFixed(1);
        if (diff !== 0) {
          const cls = diff > 0 ? 'up' : 'down';
          const sign = diff > 0 ? '+' : '';
          deltaHtml = `<span class="home-chart-info-delta ${cls}">${sign}${diff} kg</span>`;
        }
      }
    } else if (window.S.profile?.peso != null) {
      peso = window.S.profile.peso;
    }
    if (peso != null) {
      parts.push(`<div class="home-chart-info-item"><span class="k">Peso</span><span class="v">${peso}<small>kg</small></span>${deltaHtml}</div>`);
    }
    if (window.S.profile?.kcal_target) {
      parts.push(`<div class="home-chart-info-item"><span class="k">Target</span><span class="v">${window.S.profile.kcal_target}<small>kcal</small></span></div>`);
    }
    if (window.S.profile?.giorni_allenamento) {
      const g = window.S.profile.giorni_allenamento;
      parts.push(`<div class="home-chart-info-item"><span class="k">Settimana</span><span class="v">${g}<small>allenam.</small></span></div>`);
    }
    chartInfo.innerHTML = parts.join('');
  }
}

// ─── MINI CHART PESO ───────────────────────────────────────────────────────
let homeChartInst = null;

export function renderHomeMiniChart() {
  const card = document.getElementById('homeChartCard');
  const canvas = document.getElementById('homeWeightChart');
  const hint = document.getElementById('homeChartHint');
  const pts = window.S.measurements.filter(m => m.peso_kg != null).slice(0, 10).reverse();
  // v9: chart card SEMPRE visibile se profilo c'è (info inline header sempre
  // rilevante). Canvas nascosto se <2 misurazioni (1 solo punto = niente da
  // plottare) → hint discreto invita ad aggiungere misure. Con ≥2 misure il
  // chart appare con assi X+Y completi (anche se sono 2 punti, il segmento
  // mostra già trend).
  if (window.S.profile) card.classList.add('shown'); else card.classList.remove('shown');
  if (pts.length < 2) {
    if (canvas) canvas.style.display = 'none';
    if (hint) hint.style.display = '';
    document.getElementById('homeChartLast').textContent = '';
    return;
  }
  if (canvas) canvas.style.display = '';
  if (hint) hint.style.display = 'none';
  const last = pts[pts.length - 1];
  document.getElementById('homeChartLast').textContent = last.peso_kg + ' kg';
  const labels = pts.map(m => new Date(m.measured_at).toLocaleDateString('it-IT', {day:'2-digit',month:'2-digit'}));
  const values = pts.map(m => m.peso_kg);
  // Y axis: range minimo 1kg, ma adattivo se delta dati > 1kg (pad 10%).
  // Arrotondiamo yMin/yMax al mezzo kg (0.5) per allinearli ai tick standard
  // di Chart.js, così non compare un'etichetta extra di bordo (es. 62.4 sotto
  // a 62.5) che si sovrappone visivamente.
  const dMin = Math.min(...values), dMax = Math.max(...values);
  const delta = dMax - dMin;
  const pad = Math.max((1 - delta) / 2, delta * 0.1);
  const yMin = Math.floor((dMin - pad) * 2) / 2;
  const yMax = Math.ceil((dMax + pad) * 2) / 2;
  const ctx = document.getElementById('homeWeightChart').getContext('2d');
  if (homeChartInst) { homeChartInst.destroy(); homeChartInst = null; }
  const isDark = window.matchMedia('(prefers-color-scheme:dark)').matches;
  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const textColor = isDark ? '#666' : '#999';
  homeChartInst = new Chart(ctx, {
    type: 'line',
    data: { labels, datasets: [{ data: values, borderColor:'#1D9E75', backgroundColor:'rgba(29,158,117,0.1)', fill:true, tension:0.3, pointRadius:3, pointBackgroundColor:'#25c98f', borderWidth:1.5 }] },
    options: { responsive:true, maintainAspectRatio:false, plugins:{ legend:{ display:false } }, scales:{ x:{ grid:{ color:gridColor }, ticks:{ color:textColor, font:{ size:10 }, maxTicksLimit:6 } }, y:{ min:yMin, max:yMax, grid:{ color:gridColor }, ticks:{ color:textColor, font:{ size:10 }, maxTicksLimit:4 } } } }
  });
}

// ─── PLAN TILES ────────────────────────────────────────────────────────────
const TILE_MAP = { meal: 'Meal', work: 'Work', str: 'Str', spesa: 'Spesa' };
const TILE_LABEL = { meal: 'alimentazione', work: 'allenamento', str: 'stretching', spesa: 'spesa' };
const TILE_OPEN = { meal: 'meal', work: 'workout', str: 'stretch', spesa: 'spesa' };

export function setTile(k, status, message, progressPct) {
  const suffix = TILE_MAP[k]; if (!suffix) return;
  const statusEl = document.getElementById('tile' + suffix + 'Status');
  const btnEl = document.getElementById('tile' + suffix + 'Btn');
  const progWrap = document.getElementById('tile' + suffix + 'ProgWrap');
  const progBar = document.getElementById('tile' + suffix + 'Prog');
  const miniEl = document.getElementById('tile' + suffix + 'Mini');
  if (!statusEl || !btnEl) return;
  const tile = document.getElementById('tile' + suffix);
  if (tile && typeof window._attachPointerFx === 'function') window._attachPointerFx(tile);
  // Plan-tile mini visibile solo quando status === 'ok' (rich variant).
  // Per 'gen'/'err'/'empty' nascondiamo mini e mostriamo status testuale.
  if (miniEl) {
    if (status === 'ok') {
      // mini sarà popolato + mostrato da renderPlanTilesMini() async
      statusEl.style.display = 'none';
    } else {
      miniEl.style.display = 'none';
      statusEl.style.display = '';
    }
  }
  statusEl.className = 'plan-tile-status ' + status;
  statusEl.textContent = message || '';
  if (progressPct != null) {
    progWrap.style.display = '';
    progBar.style.width = Math.max(0, Math.min(100, progressPct)) + '%';
  } else {
    progWrap.style.display = 'none';
    progBar.style.width = '0';
  }
  btnEl.onclick = null;
  if (status === 'ok') {
    btnEl.disabled = false;
    btnEl.textContent = 'Apri piano';
    btnEl.className = 'plan-tile-cta primary';
    btnEl.onclick = () => { try { if (typeof logEvent === 'function') logEvent('tile_tap', { tile: k }); } catch (_) {} openPlan(TILE_OPEN[k]); };
  } else if (status === 'err') {
    btnEl.disabled = !!getBusy();
    btnEl.textContent = '↺ Riprova';
    btnEl.className = 'plan-tile-cta danger';
    btnEl.onclick = () => retryPlan(TILE_LABEL[k]);
  } else if (status === 'gen') {
    btnEl.disabled = true;
    btnEl.textContent = 'Generazione…';
    btnEl.className = 'plan-tile-cta';
  } else if (status === 'integrated') {
    btnEl.disabled = true;
    btnEl.textContent = 'Integrato nel workout';
    btnEl.className = 'plan-tile-cta';
  } else if (status === 'queue') {
    btnEl.disabled = true;
    btnEl.textContent = 'In coda…';
    btnEl.className = 'plan-tile-cta';
  } else {
    btnEl.disabled = true;
    btnEl.textContent = 'Apri piano';
    btnEl.className = 'plan-tile-cta';
  }
}

export function updateDL() {
  document.getElementById('dl').classList.add('show');
  const stretchPref = window.S.profile?.stretching_preference || 'standalone';
  // Retrocompat: se il piano meal legacy ha prep+lista ma S.plans.spesa è null,
  // l'utente può comunque aprire la spesa dai dati legacy (vedi openPlan)
  const legacySpesa = !window.S.plans.spesa && window.S.plans.meal && (window.S.plans.meal.lista || window.S.plans.meal.prep);
  const pairs = [
    ['meal', window.S.plans.meal, window.PS.meal],
    ['work', window.S.plans.workout, window.PS.work],
    ['str',  window.S.plans.stretch, window.PS.str],
    ['spesa', window.S.plans.spesa || (legacySpesa ? window.S.plans.meal : null), window.PS.spesa]
  ];
  pairs.forEach(([k, data, state]) => {
    if (state === 'gen') return; // non sovrascrivere progress in corso
    if (k === 'str' && stretchPref === 'integrato') {
      setTile('str', 'integrated', 'Incluso nell\'allenamento');
      return;
    }
    // Priorità 'err' > 'ok' con dati: se l'ultima generazione è fallita
    // mostriamo errore anche se in DB resta il piano vecchio (probabilmente
    // rotto), così l'utente capisce che deve Riprovare.
    if (state === 'err') setTile(k, 'err', 'Generazione non riuscita. Riprova quando vuoi.');
    else if (data) setTile(k, 'ok', 'Pronto');
    else setTile(k, 'empty', 'Non ancora generato');
  });
  // Refresh widget "Oggi" + plan-tile mini (info macro) — fire-and-forget
  renderHomeWidgets().catch(e => console.warn('[home] renderHomeWidgets failed:', e));
  renderPlanTilesMini().catch(e => console.warn('[home] renderPlanTilesMini failed:', e));
}

// ─── PROFILE BADGE ─────────────────────────────────────────────────────────
export function updateBadge() {
  const name = window.S.profile?.nome || window.currentUser?.email?.split('@')[0] || '…';
  const initial = name.charAt(0).toUpperCase();
  document.getElementById('headerAvatar').textContent = initial;
  document.getElementById('headerName').textContent = name;
  document.getElementById('drawerAvatar').textContent = initial;
  document.getElementById('drawerName').textContent = name;
  const btn = document.getElementById('userMenuBtn');
  if (window.S.profile) { btn.classList.add('active'); } else { btn.classList.remove('active'); }
}

// ─── HOME WIDGETS "OGGI" + plan-tile mini ─────────────────────────────────
// Spec: docs/superpowers/specs/2026-05-15-home-widgets-refactor-design.md
// Logica delegata a coach/src/plan-helpers.js (funzioni pure, testabili).

// Fetch async stats + raw completions (per riuso fra widget e plan-tile).
// Una sola fetch round trip per render, no duplicate calls.
//
// Restituisce {
//   mealCompleted, mealTotal,
//   workoutSessionsDone, workoutSessionsTarget,
//   stretchStreak, stretchCompletedToday,
//   shoppingCompleted, shoppingTotal,
//   _raw: { workoutCompletions, stretchCompletions } (per renderHomeWidgets)
// }
async function fetchHomeStats() {
  const today = new Date();
  const stats = {
    mealCompleted: 0,
    mealTotal: 0,
    workoutSessionsDone: 0,
    workoutSessionsTarget: window.S.profile?.giorni_allenamento || 3,
    stretchStreak: 0,
    stretchCompletedToday: false,
    shoppingCompleted: 0,
    shoppingTotal: 0,
    _raw: { workoutCompletions: [], stretchCompletions: [] }
  };

  // Pre-compute totals from in-memory plans (no async)
  if (window.S.plans?.meal) stats.mealTotal = totalMealsInPlan(window.S.plans.meal);
  const shoppingPlan = window.S.plans?.spesa || window.S.plans?.meal;
  if (shoppingPlan) stats.shoppingTotal = totalShoppingItems(shoppingPlan);

  const promises = [];

  // Meal: count this week ISO da meal_completions
  if (typeof window._loadMealCompletions === 'function' && window.S.plans?.meal?._id) {
    promises.push(
      Promise.resolve(window._loadMealCompletions()).then(rows => {
        stats.mealCompleted = countMealsThisWeek(rows || [], today);
      }).catch(() => {})
    );
  }

  // Workout: sessioni questa settimana ISO (100% phase-day combo complete)
  if (typeof window._loadWorkoutCompletions === 'function' && window.S.plans?.workout?._id) {
    promises.push(
      Promise.resolve(window._loadWorkoutCompletions()).then(rows => {
        stats._raw.workoutCompletions = rows || [];
        stats.workoutSessionsDone = countWorkoutSessionsThisWeek(window.S.plans.workout, rows || [], today);
      }).catch(() => {})
    );
  }

  // Stretch: streak Duolingo-style (grace until midnight, soglia 100%)
  if (typeof window._loadStretchCompletions === 'function' && window.S.plans?.stretch?._id) {
    promises.push(
      Promise.resolve(window._loadStretchCompletions()).then(rows => {
        stats._raw.stretchCompletions = rows || [];
        const s = computeStretchStreak(window.S.plans.stretch, rows || [], today);
        stats.stretchStreak = s.streak;
        stats.stretchCompletedToday = s.completedToday;
      }).catch(() => {})
    );
  }

  // Shopping: X/Y articoli spuntati (nuova tabella shopping_completions)
  if (typeof window._loadShoppingCompletions === 'function' && shoppingPlan?._id) {
    promises.push(
      Promise.resolve(window._loadShoppingCompletions()).then(rows => {
        stats.shoppingCompleted = countShoppingCompletions(shoppingPlan, rows || []);
      }).catch(() => {})
    );
  }

  await Promise.all(promises);
  return stats;
}

// Render widget Oggi (kcal/workout/stretch) usando stats già calcolate.
// Chiamato da renderHomeWidgets() dopo aver fetchato gli stats.
function _renderWidgetsSync(stats, today) {
  // ── Widget Kcal ──
  // (Bug fix 2026-05-15: rimossa riga kcalBar.style.width — DOM widgetKcalBar
  // non esiste in design v9, causava TypeError → crash silenzioso del render
  // dei widget workout/stretch successivi.)
  const kcalTarget = window.S.profile.kcal_target;
  const kcalNum = document.getElementById('widgetKcalNum');
  const kcalMeta = document.getElementById('widgetKcalMeta');
  const kcalBtn = document.getElementById('widgetKcalBtn');
  if (kcalNum && kcalTarget) {
    kcalNum.textContent = kcalTarget.toLocaleString('it-IT');
    if (kcalMeta) kcalMeta.innerHTML = '<strong>kcal target</strong> · oggi';
    if (kcalBtn) {
      kcalBtn.disabled = false;
      kcalBtn.innerHTML = '<span class="play">▶</span>Apri piano';
      kcalBtn.onclick = () => {
        try { if (typeof logEvent === 'function') logEvent('home_widget_meal_open', {}); } catch (_) {}
        if (typeof window.openPlan === 'function') window.openPlan('meal');
      };
    }
  } else if (kcalNum) {
    kcalNum.textContent = '—';
    if (kcalBtn) { kcalBtn.disabled = true; kcalBtn.onclick = null; }
  }

  // ── Widget Workout — A+C+B blended (calendar + override + sequence fallback) ──
  const wNum = document.getElementById('widgetWorkoutNum');
  const wMeta = document.getElementById('widgetWorkoutMeta');
  const wBtn = document.getElementById('widgetWorkoutBtn');
  if (wNum && wMeta && wBtn) {
    const todayWk = getTodayWorkout(window.S.plans?.workout, window.S.profile, stats._raw.workoutCompletions, today);
    if (todayWk.kind === 'workout') {
      const day = todayWk.dayData || {};
      const name = day.name || day.t || day.titolo || day.gruppo || 'Workout';
      const sub = day.sub || day.subtitle || '';
      const min = window.S.profile.minuti_sessione;
      wNum.textContent = name.length > 16 ? name.slice(0, 14) + '…' : name;
      wMeta.textContent = sub ? sub : (min ? `${min} min · sessione` : 'sessione settimanale');
      wBtn.disabled = false;
      wBtn.innerHTML = '<span class="play">▶</span>Avvia';
      wBtn.onclick = () => {
        try { if (typeof logEvent === 'function') logEvent('home_widget_workout_open', { mode: todayWk.mode }); } catch (_) {}
        if (typeof window.openPlan === 'function') window.openPlan('workout');
      };
    } else if (todayWk.kind === 'rest') {
      wNum.textContent = '🛋️';
      wMeta.textContent = todayWk.message;
      wBtn.disabled = true;
      wBtn.onclick = null;
    } else if (todayWk.kind === 'end') {
      wNum.textContent = '🎉';
      wMeta.textContent = 'Piano completato';
      wBtn.disabled = false;
      wBtn.innerHTML = '<span class="play">↻</span>Rigenera';
      wBtn.onclick = () => {
        if (typeof window.retryPlan === 'function') window.retryPlan('allenamento');
      };
    } else {
      wNum.textContent = '—';
      wMeta.textContent = 'piano non generato';
      wBtn.disabled = true;
      wBtn.onclick = null;
    }
  }

  // ── Widget Stretch — perpetuo, day = ISO weekday-1, 100% threshold ──
  const sPlan = window.S.plans?.stretch;
  const sNum = document.getElementById('widgetStretchNum');
  const sMeta = document.getElementById('widgetStretchMeta');
  const sBtn = document.getElementById('widgetStretchBtn');
  if (sNum && sMeta && sBtn) {
    if (Array.isArray(sPlan) && sPlan.length > 0) {
      const sDayIdx = getStretchDayIdxFor(today);
      const sDay = sPlan[sDayIdx] || sPlan[0];
      const completedToday = isStretchDayCompleteOn(sPlan, stats._raw.stretchCompletions, sDayIdx, today);
      if (completedToday) {
        sNum.textContent = '✓';
        sMeta.textContent = 'Completato oggi · ottimo!';
        sBtn.disabled = false;
        sBtn.innerHTML = '<span class="play">▶</span>Riapri';
      } else {
        const min = sDay.durata || sDay.min || sDay.dur || sDay.duration || 5;
        sNum.innerHTML = `${min}<small>min</small>`;
        sMeta.textContent = sDay.tag || sDay.name || 'sessione di oggi';
        sBtn.disabled = false;
        sBtn.innerHTML = '<span class="play">▶</span>Avvia';
      }
      sBtn.onclick = () => {
        try { if (typeof logEvent === 'function') logEvent('home_widget_stretch_open', {}); } catch (_) {}
        if (typeof window.openPlan === 'function') window.openPlan('stretch');
      };
    } else {
      sNum.textContent = '—';
      sMeta.textContent = 'piano non generato';
      sBtn.disabled = true;
      sBtn.onclick = null;
    }
  }

  // Pointer fx
  if (typeof window._attachPointerFx === 'function') {
    document.querySelectorAll('.home-widget').forEach(w => window._attachPointerFx(w));
  }
}

export async function renderHomeWidgets() {
  const row = document.getElementById('homeWidgetsRow');
  const label = document.getElementById('homeWidgetsLabel');
  if (!row || !label) return;

  // Hide widgets se utente non ha profilo (onboarding pending)
  if (!window.S.profile) {
    row.style.display = 'none';
    label.style.display = 'none';
    return;
  }
  row.style.display = '';
  label.style.display = '';

  const today = new Date();
  const stats = await fetchHomeStats();
  // Salva stats per riuso da renderPlanTilesMini (no duplicate fetch)
  window.S._homeStats = stats;
  window.S._homeStatsAt = Date.now();
  _renderWidgetsSync(stats, today);
}

// ─── PLAN TILE MINI (info macro: pasti/sessioni/streak/articoli) ───────────
// Aggiorna la sezione `.plan-tile-mini` con info macro del piano. Chiamato
// da setTile() quando status === 'ok'. Async perché alcuni dati richiedono
// fetch completion stats.
// Render plan-tile mini (info macro). Riusa stats da renderHomeWidgets se
// disponibili (< 5s fa), altrimenti rifetch.
export async function renderPlanTilesMini() {
  if (!window.S.plans) return;

  // Riusa stats cached se freschi (<5s), altrimenti fetch
  let stats = window.S._homeStats;
  const freshCutoff = Date.now() - 5000;
  if (!stats || !window.S._homeStatsAt || window.S._homeStatsAt < freshCutoff) {
    stats = await fetchHomeStats();
    window.S._homeStats = stats;
    window.S._homeStatsAt = Date.now();
  }

  // ALIMENTAZIONE — pasti completati this week ISO / totali piano
  const mealMini = document.getElementById('tileMealMini');
  const mealStatus = document.getElementById('tileMealStatus');
  if (mealMini && window.S.plans.meal && stats.mealTotal > 0) {
    document.getElementById('tileMealNum').innerHTML = `${stats.mealCompleted}<small>/${stats.mealTotal}</small>`;
    document.getElementById('tileMealLabel').textContent = 'pasti settimana';
    document.getElementById('tileMealBar').style.width = `${Math.min(100, (stats.mealCompleted / stats.mealTotal) * 100)}%`;
    mealMini.style.display = '';
    if (mealStatus) mealStatus.style.display = 'none';
  }

  // ALLENAMENTO — sessioni questa settimana / giorni_allenamento target
  const workMini = document.getElementById('tileWorkMini');
  const workStatus = document.getElementById('tileWorkStatus');
  if (workMini && window.S.plans.workout) {
    document.getElementById('tileWorkNum').innerHTML = `${stats.workoutSessionsDone}<small>/${stats.workoutSessionsTarget}</small>`;
    document.getElementById('tileWorkLabel').textContent = 'sessioni settimana';
    document.getElementById('tileWorkBar').style.width = `${Math.min(100, (stats.workoutSessionsDone / stats.workoutSessionsTarget) * 100)}%`;
    workMini.style.display = '';
    if (workStatus) workStatus.style.display = 'none';
  }

  // STRETCHING — streak Duolingo-style (grace until midnight, 100% threshold)
  const strMini = document.getElementById('tileStrMini');
  const strStatus = document.getElementById('tileStrStatus');
  if (strMini && window.S.plans.stretch) {
    document.getElementById('tileStrNum').innerHTML = `${stats.stretchStreak}<small>gg</small>`;
    document.getElementById('tileStrLabel').textContent = 'streak consecutivo';
    document.getElementById('tileStrBar').style.width = `${Math.min(100, stats.stretchStreak * 14)}%`; // 7gg = 100%
    strMini.style.display = '';
    if (strStatus) strStatus.style.display = 'none';
  }

  // SPESA — articoli spuntati / totali (shopping_completions)
  const shopMini = document.getElementById('tileSpesaMini');
  const shopStatus = document.getElementById('tileSpesaStatus');
  if (shopMini && (window.S.plans.spesa || window.S.plans.meal) && stats.shoppingTotal > 0) {
    document.getElementById('tileSpesaNum').innerHTML = `${stats.shoppingCompleted}<small>/${stats.shoppingTotal}</small>`;
    document.getElementById('tileSpesaLabel').textContent = 'articoli lista';
    document.getElementById('tileSpesaBar').style.width = `${Math.min(100, (stats.shoppingCompleted / stats.shoppingTotal) * 100)}%`;
    shopMini.style.display = '';
    if (shopStatus) shopStatus.style.display = 'none';
  }
}

// ─── CHART METRIC DROPDOWN ─────────────────────────────────────────────────
// Per ora solo "weight" funzionante. Le altre metriche (kcal/workout/stretch)
// sono disabled nel select. BKL future per renderle aggregando i completion
// per data e plottandoli con Chart.js bar/line.
export function initHomeChartSwitcher() {
  const sel = document.getElementById('homeChartMetric');
  if (!sel) return;
  sel.addEventListener('change', () => {
    const metric = sel.value;
    if (metric === 'weight') {
      renderHomeMiniChart();
    } else {
      // Placeholder per metriche future — disabled in HTML, ma se mai abilitate
      // qui chiamerà render specifico (renderKcalChart, renderWorkoutChart, etc.)
      console.log('[home-chart] metric', metric, 'not yet implemented');
    }
  });
}
